import React, { useMemo } from 'react';
import { Box, Chip, Typography, Button } from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CloseIcon from '@mui/icons-material/Close';

const AppliedFilterChips = ({
  filters = {},
  filterConfig = [],
  optionsMap = {},
  onRemove,
  onClearAll,
}) => {
  const getDisplayValue = (field, value) => {
    if (value === undefined || value === null || value === '' || value === 'All') return null;
    if (Array.isArray(value) && value.length === 0) return null;

    const options = optionsMap[field.key] || field.staticOptions || [];

    if (field.type === 'multi_select') {
      if (options.length > 0 && value.length === options.length) return 'All';
      return value
        .map((id) => options.find((o) => o.id === id)?.name || id)
        .join(', ');
    }

    if (field.type === 'select' || field.type === 'enum') {
      const selectOptions = field.type === 'enum' 
        ? (field.enumOptions || []).map(o => ({ id: o.value, name: o.label }))
        : options;
      
      const found = selectOptions.find((o) => o.id === value);
      return found ? found.name : value;
    }

    if (field.type === 'date_range') {
      if (!value.from && !value.to) return null;
      const from = value.from ? new Date(value.from).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '...';
      const to = value.to ? new Date(value.to).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '...';
      return `${from} - ${to}`;
    }

    return String(value);
  };

  const activeFilters = useMemo(() => {
    return filterConfig
      .map((field) => ({
        field,
        displayValue: getDisplayValue(field, filters[field.key]),
      }))
      .filter((f) => f.displayValue);
  }, [filters, filterConfig, optionsMap]);

  if (activeFilters.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, py: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
        <FilterAltIcon sx={{ fontSize: 16, color: 'primary.main', mr: 0.5 }} />
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 700,
            color: '#475467',
            letterSpacing: '0.05em',
            textTransform: 'uppercase'
          }}
        >
          Active Filters
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {activeFilters.map(({ field, displayValue }) => (
          <Chip
            key={field.key}
            size="medium"
            variant="outlined"
            label={
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography component="span" sx={{ fontWeight: 600, color: '#344054', fontSize: '13px' }}>
                  {field.label}:
                </Typography>
                <Typography component="span" sx={{ fontWeight: 500, color: 'primary.main', fontSize: '13px' }}>
                  {displayValue}
                </Typography>
              </Box>
            }
            onDelete={() => onRemove?.(field.key)}
            sx={{
              borderRadius: '8px',
              borderColor: '#EAECF0',
              bgcolor: '#F9FAFB',
              height: '32px',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: '#F2F4F7',
                borderColor: '#D0D5DD',
              },
              '& .MuiChip-deleteIcon': { 
                fontSize: 16,
                color: '#98A2B3',
                '&:hover': { color: '#667085' }
              },
            }}
          />
        ))}

        {onClearAll && (
          <Button
            size="small"
            onClick={onClearAll}
            startIcon={<CloseIcon sx={{ fontSize: '16px !important' }} />}
            sx={{
              fontSize: '13px',
              fontWeight: 600,
              textTransform: 'none',
              color: '#667085',
              px: 1.5,
              borderRadius: '8px',
              '&:hover': { 
                bgcolor: '#FEF3F2', 
                color: '#B42318',
                '& .MuiButton-startIcon': { color: '#B42318' }
              },
            }}
          >
            Clear All
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default AppliedFilterChips;
